<template>
  <DetailCard :loading="loading" :error="errorMessage">
    <template #heading>
      <span class="pi pi-exclamation-octagon"></span>
      <a :href="restrictionsExternalUrl" target="_blank">
        {{ $t(`messages.detail.restrictions.title`) }}
      </a>
    </template>
    <template #controls>
      <MapHelpTooltip size="sm" variant="secondary" />
    </template>
    <template #notices>
      <div v-if="errorMessage" class="mt-2">
        <Message severity="error" class="text-center max-w-fit">{{ errorMessage }}</Message>
      </div>
      <p v-else-if="fallbackNote" class="mt-2 mb-0 text-sm text-gray-500 dark:text-gray-400">
        {{ fallbackNote }}
      </p>
    </template>
    <div id="detail-restrictions-map" class="detail-map">
      <MapComponent
        :center="mapview.center"
        :extent="mapview.extent"
        :zoom="mapview.zoom"
        :bbox="mapview.bbox"
        :minZoom="mapview.minZoom"
        :maxZoom="mapview.maxZoom"
        :projection="mapview.projection"
        :baseLayers="mapview.baseLayers"
        :infoLayers="mapview.infoLayers"
        :overlay="mapview.overlay"
        :zoomControl="true"
        :twoFingerPanZoom="true"
        @update="exportMap"
        @ready="onMapReady"
        ref="map"
      />
    </div>
    <!-- Custom nested grouping: liik (static) -> nahtuseLiik (collapsible) -->
    <div v-if="hierarchicalData.length > 0" class="restrictions-grouped">
      <div v-for="liikGroup in hierarchicalData" :key="liikGroup.liik">
        <!-- Static liik section header -->
        <div
          class="bg-gray-100 dark:bg-gray-700 px-4 py-2 font-bold text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between"
        >
          <span>{{ liikGroup.liik }}</span>
          <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Ulatus (m²)</span>
        </div>

        <!-- nahtuseLiik groups -->
        <div v-for="nahtusGroup in liikGroup.children" :key="nahtusGroup.nahtuseLiik" class="border-b border-gray-100 dark:border-gray-700">
          <!-- Single row: non-expandable, show value inline -->
          <div
            v-if="nahtusGroup.rows.length === 1"
            class="w-full flex items-center gap-2 px-4 py-2"
          >
            <!-- Invisible spacers to align with expandable rows -->
            <span class="pi pi-chevron-right invisible" />
            <Badge :value="1" size="small" class="bg-primary-600/70 text-white" />
            <span class="font-medium text-gray-600 dark:text-gray-300">
              {{ nahtusGroup.nahtuseLiik }}
            </span>
            <span class="ml-auto text-sm text-gray-500 dark:text-gray-400 tabular-nums">
              {{ nahtusGroup.rows[0]?.pindala }} m²
            </span>
          </div>

          <!-- Multiple rows: expandable -->
          <template v-else>
            <!-- Clickable nahtuseLiik header -->
            <button
              type="button"
              class="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              @click="toggleGroup(liikGroup.liik, nahtusGroup.nahtuseLiik)"
            >
              <span
                class="pi pi-chevron-right text-primary-500 dark:text-gray-400 transition-transform duration-300 ease-out"
                :class="{ 'rotate-90': isGroupExpanded(liikGroup.liik, nahtusGroup.nahtuseLiik) }"
              />
              <Badge :value="nahtusGroup.rows.length" size="small" class="bg-primary-600/70 text-white" />
              <span class="font-medium text-gray-600 dark:text-gray-300">
                {{ nahtusGroup.nahtuseLiik }}
              </span>
              <span class="ml-auto text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                {{ nahtusGroup.rows[0]?.pindala }} m²
              </span>
            </button>

            <!-- Expandable content with rows -->
            <div
              class="grid transition-[grid-template-rows] duration-300 ease-out"
              :class="isGroupExpanded(liikGroup.liik, nahtusGroup.nahtuseLiik) ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
            >
              <div class="overflow-hidden">
                <div class="bg-white dark:bg-gray-900 flex justify-end">
                  <div class="w-full">
                    <div
                      v-for="(row, idx) in nahtusGroup.rows"
                      :key="idx"
                      class="px-4 py-2 text-right text-sm text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      {{ row.pindala }} m²
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </DetailCard>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDetailMapHelpers } from '@/composables/useDetailMapHelpers'
import {
  DETAIL_RESTRICTION_MAP_INFO_LAYERS,
  MAP_CENTER,
  MAP_EXTENT,
  MAP_MAXZOOM,
  MAP_MINZOOM,
  MAP_PROJECTION,
} from '@/constants'
import { transform } from '@/services/map/ProjectionService'
import { useDetailStore } from '@/stores/useDetailStore'
import MapComponent from '../../map/MapComponent.vue'
import MapHelpTooltip from '../../map/MapHelpTooltip.vue'
import DetailCard from '../DetailCard.vue'

const { t } = useI18n()

const store = useDetailStore()
const { cadasterId, landBoard, restrictions } = storeToRefs(store)

const { findLayer } = useDetailMapHelpers()

const loading = shallowRef(true)
const errorMessage = shallowRef('')
// Set when the backend served persisted rows instead of the live X-Road
// answer (RED-352): { source: 'bulk' | 'xroad', asOf: 'YYYY-MM-DD' }.
const fallback = shallowRef(null)
const restrictionsExternalUrl = shallowRef('#')
const expandedGroups = ref(new Set())
const map = ref(null)

// Transform flat data into hierarchical structure: liik -> nahtuseLiik -> rows
const hierarchicalData = computed(() => {
  if (!restrictions.value?.length) return []

  const liikMap = new Map()

  for (const item of restrictions.value) {
    const liik = item.liik || ''
    const nahtuseLiik = item.nahtuseLiik || ''

    if (!liikMap.has(liik)) {
      liikMap.set(liik, new Map())
    }

    const nahtusMap = liikMap.get(liik)
    if (!nahtusMap.has(nahtuseLiik)) {
      nahtusMap.set(nahtuseLiik, [])
    }

    nahtusMap.get(nahtuseLiik).push(item)
  }

  // Convert to array structure, sorted
  return Array.from(liikMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([liik, nahtusMap]) => ({
      liik,
      children: Array.from(nahtusMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([nahtuseLiik, rows]) => ({
          nahtuseLiik,
          rows,
        })),
    }))
})

const fallbackNote = computed(() => {
  if (!fallback.value) return ''
  const [y, m, d] = fallback.value.asOf.split('-')
  const date = `${d}.${m}.${y}`
  return fallback.value.source === 'bulk'
    ? t('messages.detail.restrictions.fallbackBulk', { date })
    : t('messages.detail.restrictions.fallbackStored', { date })
})

const getGroupKey = (liik, nahtuseLiik) => `${liik}|${nahtuseLiik}`

const isGroupExpanded = (liik, nahtuseLiik) => {
  return expandedGroups.value.has(getGroupKey(liik, nahtuseLiik))
}

const toggleGroup = (liik, nahtuseLiik) => {
  const key = getGroupKey(liik, nahtuseLiik)
  const newSet = new Set(expandedGroups.value)
  if (newSet.has(key)) {
    newSet.delete(key)
  } else {
    newSet.add(key)
  }
  expandedGroups.value = newSet
}

const mapview = ref({
  status: true,
  center: transform('fromLonLat', MAP_CENTER, MAP_PROJECTION),
  zoom: 3,
  extent: transform('extent', MAP_EXTENT, 'EPSG:4326', MAP_PROJECTION),
  minZoom: MAP_MINZOOM,
  maxZoom: MAP_MAXZOOM,
  projection: MAP_PROJECTION,
  baseLayers: [findLayer('ma-photo')],
  infoLayers: DETAIL_RESTRICTION_MAP_INFO_LAYERS.map(layer => findLayer(layer)),
  overlay: null,
  // Seeded from the store so MapComponent fits the parcel BEFORE its first
  // rendered frame — a null bbox here means an Estonia-scale frame of WMS
  // tiles that gets thrown away when onMapReady fits 200ms later.
  bbox: landBoard.value?.bbox ?? null,
})

const loadRestrictionsData = async cadasterId => {
  restrictionsExternalUrl.value =
    'https://kitsendused.maaamet.ee/#/avalik;ky=' + cadasterId

  loading.value = true
  await axios
    .get('/xroad/restrictions/' + cadasterId)
    .then(response => {
      // The body stays the plain array the docx export reads; provenance
      // of a fallback answer travels in headers only.
      restrictions.value = response.data
      const source = response.headers?.['x-restrictions-source']
      const asOf = response.headers?.['x-restrictions-as-of']
      fallback.value = source && asOf ? { source, asOf } : null
    })
    .catch(() => {
      errorMessage.value = t('messages.detail.restrictions.serviceError')
    })
    .finally(() => {
      loading.value = false
    })
}

const exportMap = async () => {
  if (!map.value) {
    return
  }
  const mapExport = await map.value.exportMap()
  store.setMapExport({ key: 'restriction', value: mapExport })
}

const onMapReady = () => {
  // Fallback for landBoard arriving after mount — the usual path is the
  // seeded mapview.bbox, fitted at map creation.
  if (landBoard.value?.bbox && !mapview.value.bbox) {
    mapview.value.bbox = landBoard.value.bbox

    if (map.value) {
      map.value.updateBbox(landBoard.value.bbox)
    }
  }
  loading.value = false
}

onMounted(() => {
  if (cadasterId.value) {
    restrictionsExternalUrl.value =
      'https://kitsendused.maaamet.ee/#/avalik;ky=' + cadasterId.value
    loadRestrictionsData(cadasterId.value)
  }
})
</script>

<style scoped>
  .detail-map {
    position: relative;
    height: 600px;
    width: 100%;
    margin: 0;
  }

  /* Fade-slide transition for text swap */
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: all 0.2s ease-out;
  }

  .fade-slide-enter-from {
    opacity: 0;
    transform: translateY(-4px);
  }

  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(4px);
  }
</style>
